/**
 *
 * KitStartTtl
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 26-Sep-2025
 */

import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import PortalLoader from '@nexidigital/portalloader'
import MerchantModal from '@nexidigital/merchantmodal'
import KitStartCard from '../../../KitStart/components/KitStartCard'
import { getAvailableServices } from '../../../KitStart/utils/navigationEngine'
import formattedMessage from './languageModule.jsx'
import './KitStartTtl.scss'

const KitStartTtl = props => {
  const {
    // TTL Store data
    kitStartData,
    kitStartLoading,

    // Actions
    actions
  } = props

  const navigate = useNavigate()

  // Load KitStart data when component mounts
  useEffect(() => {
    if (!kitStartData?.featureService) {
      actions.doGetKitStartData && actions.doGetKitStartData()
    }
  }, [kitStartData, actions])

  // Auto-navigate if no services available
  useEffect(() => {
    if (kitStartData?.featureService?.length === 0 && !kitStartLoading) {
      navigate('/')
    }
  }, [kitStartData, kitStartLoading, navigate])

  // Handle close TTL popup
  const handleClose = () => {
    actions.doHideTtl && actions.doHideTtl()
  }

  // Handle primary action - navigate to KitStart with TTL context
  const handlePrimaryAction = () => {
    navigate('/kitstart', { state: { fromTTL: true } })
    actions.doHideTtl && actions.doHideTtl()
  }

  // Get available services for KitStart usando la nuova logica T0/T1/T2
  const getAvailableServicesForTtl = () => {
    if (!kitStartData?.featureService) return []

    // Usa la funzione del navigationEngine che implementa la logica T0/T1/T2
    return getAvailableServices(kitStartData.featureService)
  }

  // Map services for dynamic rendering
  const renderServiceCards = () => {
    const availableServices = getAvailableServicesForTtl()

    return availableServices.map(service => {
      let serviceConfig = {}

      switch (service.featureType) {
        case 'MICROPAYMENTS':
          serviceConfig = {
            title: formattedMessage.servicesMicropaymentsTitle,
            description: formattedMessage.servicesMicropaymentsDescription,
            img: `${process.env.CMS_URL}/services/Micropayment_hero.webp`,
            altTextImg: 'Micropagamenti'
          }
          break
        case 'PAYMAIL':
          serviceConfig = {
            title: formattedMessage.servicesPblTitle,
            description: formattedMessage.servicesPblDescription,
            img: `${process.env.CMS_URL}/services/PayByLink_hero.png`,
            altTextImg: 'Pay-By-Link'
          }
          break
        case 'BACKUP_POS':
        case 'ISP_POS': // ISP_POS usa la stessa configurazione di BACKUP_POS
          serviceConfig = {
            title: formattedMessage.getServicesSoftposTitle(service.featureType),
            description: formattedMessage.getServicesSoftposDescription(
              service.additionalData?.freeMonths,
              service.additionalData?.monthFee,
              service.featureType
            ),
            img: `${process.env.CMS_URL}/services/SoftPos_hero.webp`,
            altTextImg: service.featureType === 'ISP_POS' ? 'SoftPOS aggiuntivo' : 'SoftPOS di supporto'
          }
          break
        default:
          return null
      }

      return (
        <KitStartCard
          key={service.featureType}
          title={serviceConfig.title}
          description={serviceConfig.description}
          img={serviceConfig.img}
          altTextImg={serviceConfig.altTextImg}
          service={service}
          languageModule={formattedMessage}
        />
      )
    }).filter(Boolean)
  }

  return (
    <MerchantModal
      topBarTitle=' '
      title={formattedMessage.servicesMainTitle}
      description={formattedMessage.servicesMainSubtitle}
      primaryButtonLabel={formattedMessage.servicesRequestNowLabel}
      primaryAction={handlePrimaryAction}
      onClose={handleClose}
      closeLabel={formattedMessage.servicesCloseLabel}
    >
      {kitStartLoading
        ? (
          <div className='KitStartLoader'>
            <PortalLoader centered />
          </div>
          )
        : (
          <div className={`kitStartServicesListTtl ${renderServiceCards().length === 1 ? 'singleService' : ''}`}>
            {renderServiceCards()}
          </div>
          )}
    </MerchantModal>
  )
}

KitStartTtl.propTypes = {
  /** TTL KitStart data from API containing services */
  kitStartData: PropTypes.object,
  /** TTL loading state */
  kitStartLoading: PropTypes.bool,
  /** Actions object containing TTL actions */
  actions: PropTypes.object
}

KitStartTtl.defaultProps = {
  kitStartData: {},
  kitStartLoading: false,
  actions: {}
}

export default KitStartTtl
